import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { ApiProvider } from "@src/store/ApiProvider";
import { withPasswordManagerRefresh } from "./passwordManagerRequest";

const extractErrorMessage = (error: unknown): string => {
	if (axios.isAxiosError(error)) {
		const data = error.response?.data as { message?: string; Message?: string; error?: string } | string | undefined;
		if (typeof data === "string" && data.trim()) return data;
		if (data && typeof data === "object") {
			const msg = data.message || data.Message || data.error;
			if (msg) return msg;
		}
		if (error.message) return error.message;
	}
	if (error instanceof Error && error.message) return error.message;
	return "Failed to trigger account sweep";
};

export interface ITriggerAccountSweepParams {
	credentialId: number;
}

export interface ITriggerAccountSweepResponse {
	message?: string;
	account?: string;
	status?: string;
}

interface ITriggerAccountSweepState {
	loading: boolean;
	error: string | null;
}

const initialState: ITriggerAccountSweepState = {
	loading: false,
	error: null,
};

const getAuthHeaders = () => {
	const token = localStorage.getItem("PasswordManager_accessToken") ?? "";
	return {
		Authorization: token ? `Bearer ${token}` : undefined,
		passwordManagerAccessToken: token || undefined,
		"Content-Type": "application/json",
	};
};

const buildUrl = (credentialId: number) => {
	const baseUrl = ApiProvider.default.passwordManagerBaseURL || "https://qa.stagefront.com/password-manager/api";
	return `${baseUrl.replace(/\/$/, "")}/External/credentials/${credentialId}/sweep`;
};

export const triggerAccountSweep = createAsyncThunk<ITriggerAccountSweepResponse, ITriggerAccountSweepParams>(
	"passwordManager/triggerAccountSweep",
	async (payload, { rejectWithValue, dispatch }) => {
		try {
			const res = await withPasswordManagerRefresh(dispatch, () =>
				axios.post<ITriggerAccountSweepResponse>(
					buildUrl(payload.credentialId),
					{},
					{ headers: getAuthHeaders() },
				),
			);
			return res.data ?? {};
		} catch (error: unknown) {
			return rejectWithValue(extractErrorMessage(error));
		}
	},
);

const triggerAccountSweepSlice = createSlice({
	name: "passwordManager/triggerAccountSweep",
	initialState,
	reducers: {},
	extraReducers: (builder) => {
		builder
			.addCase(triggerAccountSweep.pending, (state) => {
				state.loading = true;
				state.error = null;
			})
			.addCase(triggerAccountSweep.fulfilled, (state) => {
				state.loading = false;
				state.error = null;
			})
			.addCase(triggerAccountSweep.rejected, (state, action) => {
				state.loading = false;
				state.error = (action.payload as string) || action.error.message || "Failed to trigger account sweep";
			});
	},
});

export const triggerAccountSweepReducer = triggerAccountSweepSlice.reducer;
